import * as Sentry from '@sentry/react'
import axios, { AxiosResponse } from 'axios'
import { ApiResponseError, createApiError } from 'errors'
import { getFingerprint } from 'helpers/common'
import { OPEN_BANKING_REGION } from 'types/common'
import type { Pocket, AccessToken, OpenBankingRegion } from 'types/common'
import type { Consents } from 'constants/common'

export type RetailAuthenticationBody = {
  phone: string
  password: string
  type: 'APP' | 'SMS'
}

export type RetailVerificationBody = {
  phone: string
  verify_token: string
}

export type RetailFetchTokenBody = {
  phone: string
  password: string
  tokenId: string
}

export type AuthorizationBody = {
  verify_token: string
  response_type: string | null
  client_id: string | null
  redirect_uri: string | null
  scope: string | null
  state: string | null
  nonce: string | null
  request: string | null
  response_mode?: string
}

export type RejectionBody = {
  client_id: string | null
  redirect_uri: string | null
  state: string | null
  request: string | null
  response_mode?: string
}

export type AccountAuthorizationBody = AuthorizationBody & { consented_pockets: string[] }

export type ConsentBody = {
  client_id: string | null
  request: string | null
  consent: Consents
}

export type ApplicationData = {
  name: string
  picture?: string
  openBankingRegion: OpenBankingRegion
}

type ApplicationDataDTO = {
  name: string
  picture?: string
  openBankingRegion?: string
}

const HTTP_CODE = {
  NOT_FOUND: 404,
} as const

export class ApplicationNotFoundError extends Error {
  readonly description =
    'The provided client ID is invalid or not found. Please check your credentials and try again'

  constructor() {
    super('Application not found')
    this.name = 'ApplicationNotFoundError'
  }
}

export type ConsentResponse = {
  [Consents.ACCOUNT]: {
    pockets: object[]
  }
  [Consents.PAYMENT]: {
    merchant: string
    amount: string
  }
  [Consents.FUNDS_CONFIRMATION]: {
    scheme: string
    identification: string
  }
}

const HEADERS: any = {
  'X-Device-Id': getFingerprint(),
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Client-Version': '100.0',
}

axios.defaults.headers = HEADERS

export const authenticateRetailUser = (data: RetailAuthenticationBody) =>
  axios.post<{ tokenId: string; expiresIn: number } | void>(
    `/user/authenticate`,
    {
      ...data,
    },
    {
      headers: HEADERS,
    },
  )

export const verifyRetailUser = (
  data: RetailVerificationBody,
): Promise<{ access_token: string }> =>
  axios
    .post(
      `/user/authentication/verify`,
      {
        ...data,
      },
      {
        headers: HEADERS,
      },
    )
    .then((res: AxiosResponse) => res.data)

export const fetchPushNotificationTokenForRetailUser = (
  data: RetailFetchTokenBody,
): Promise<{ accessToken: string }> =>
  axios
    .post(
      '/user/authentication/token',
      {
        ...data,
      },
      {
        headers: HEADERS,
      },
    )
    .then((res: AxiosResponse) => res.data)

export const fetchPockets = (accessToken: AccessToken): Promise<Pocket[]> =>
  axios
    .get(`/user/pockets`, {
      headers: { ...HEADERS, Authorization: `Basic ${accessToken}` },
    })
    .then((res) => res.data)

export const authorizeAccount = (
  data: AccountAuthorizationBody,
  accessToken: AccessToken,
): Promise<{ redirect_uri: string }> =>
  axios
    .post(
      `/user/authorise/account`,
      { ...data },
      { headers: { ...HEADERS, Authorization: `Basic ${accessToken}` } },
    )
    .then((res) => res.data)

export const authorize = (
  data: AuthorizationBody,
  accessToken: AccessToken,
  consent: Consents,
): Promise<{ redirect_uri: string }> =>
  axios
    .post(
      `/user/authorise/${consent}`,
      { ...data },
      { headers: { ...HEADERS, Authorization: `Basic ${accessToken}` } },
    )
    .then((res) => res.data)

export const reject = (
  data: RejectionBody,
  accessToken: AccessToken,
  consent: Consents,
): Promise<{ redirect_uri: string }> =>
  axios
    .post(
      `/user/reject/${consent}`,
      { ...data },
      { headers: { ...HEADERS, Authorization: `Basic ${accessToken}` } },
    )
    .then((res) => res.data)

type OpenBankingRegionResult = { value: OpenBankingRegion; error: Error | null }

export const mapToOpenBankingRegion = (
  regionDto: ApplicationDataDTO['openBankingRegion'],
): OpenBankingRegionResult => {
  switch (regionDto) {
    case OPEN_BANKING_REGION.AU:
      return { value: OPEN_BANKING_REGION.AU, error: null }
    case OPEN_BANKING_REGION.OTHER:
    case undefined:
      return { value: OPEN_BANKING_REGION.OTHER, error: null }
    default:
      return {
        value: OPEN_BANKING_REGION.OTHER,
        error: new Error(`Unsupported openBankingRegion: ${regionDto}`),
      }
  }
}

type ApplicationDataResult = { value: ApplicationData; error: Error | null }

const mapToApplicationData = (data: ApplicationDataDTO): ApplicationDataResult => {
  const { value: openBankingRegion, error } = mapToOpenBankingRegion(
    data.openBankingRegion,
  )
  return {
    value: {
      name: data.name,
      picture: data.picture,
      openBankingRegion,
    },
    error,
  }
}

export const fetchAppData = async ({
  clientId,
}: {
  clientId: string
}): Promise<ApplicationData> => {
  try {
    // TODO This is backwards-compatible optimistic casting replace with Zod validation.
    // https://revolut.atlassian.net/browse/PBPI-2898
    const response = await axios.get<ApplicationDataDTO>(`/user/application/${clientId}`)
    const { value, error } = mapToApplicationData(response.data)
    if (error) {
      Sentry.captureException(error)
    }
    return value
  } catch (error: unknown) {
    const apiError = createApiError(error)

    if (
      apiError instanceof ApiResponseError &&
      apiError.httpStatusCode === HTTP_CODE.NOT_FOUND
    ) {
      throw new ApplicationNotFoundError()
    }

    Sentry.captureException(apiError)
    throw apiError
  }
}

export const fetchConsentDetails = (
  { consent, ...rest }: ConsentBody,
  accessToken: AccessToken,
) =>
  axios
    .get(`/user/consents/${consent}`, {
      headers: { ...HEADERS, Authorization: `Basic ${accessToken}` },
      params: {
        ...rest,
      },
    })
    .then((res) => res.data)
    .then((details: ConsentResponse[ConsentBody['consent']]) => {
      return {
        type: consent,
        details,
      }
    })

export const fetchRate = (
  {
    from,
    to,
    amount,
  }: {
    from: string
    to: string
    amount: number
  },
  accessToken: AccessToken,
) =>
  axios
    .get(`/user/exchange/rate/${from}${to}?amount=${amount}`, {
      headers: { ...HEADERS, Authorization: `Basic ${accessToken}` },
    })
    .then((res) => res.data)

export const fetchCountryCode = () =>
  axios.get('/user/location').then((res: AxiosResponse<{ country?: string }>) => res.data)

export default {
  authenticateRetailUser,
  authorize,
  reject,
  authorizeAccount,
  fetchAppData,
  fetchConsentDetails,
  fetchPockets,
  fetchPushNotificationTokenForRetailUser,
  fetchRate,
  verifyRetailUser,
}
